/**
 * Phase C presentation crossfade — tenant default on; warm-path dissolve when sticky would hard-cut.
 * @see docs/player-playback-scheduler.md (Frame presentation contract)
 */

import type { IQueueFrame } from '@/types'
import type { StickyHoldClearReason } from '@/helpers/holdUntilReadyPresentation'
import {
  queueFramesDifferInFacetAssets,
  sameQueueFrameIdentity
} from '@/helpers/holdUntilReadyPresentation'
import { isLowCapacityFeatureForcedOff } from '@lucimark/shared/helpers/playerLowCapacityPolicy'
import { resolvePlayerCreativeCrossfadeMs } from '@lucimark/shared/models/tenant'
import { CROSSFADE_MS } from '@/constants/playback'

export { CROSSFADE_MS }

export const PRESENTATION_CROSSFADE_STORAGE_KEY = 'LUCIMARK:PRESENTATION_CROSSFADE'

/** URL query override (same idea as other player debug flags). `1` on, `0` off. */
export const PRESENTATION_CROSSFADE_QUERY_PARAM = 'lucimark_presentation_crossfade'

/**
 * Debug override from query or localStorage, or `null` when unset (fall through to tenant / default).
 */
function readPresentationCrossfadeDebugOverride(): boolean | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const params = new URLSearchParams(window.location.search)
    const query = params.get(PRESENTATION_CROSSFADE_QUERY_PARAM)
    if (query === '1') {
      return true
    }
    if (query === '0') {
      return false
    }
  }
  catch {
    // ignore
  }
  if (typeof localStorage === 'undefined') {
    return null
  }
  try {
    const stored = localStorage.getItem(PRESENTATION_CROSSFADE_STORAGE_KEY)
    if (stored === 'false') {
      return false
    }
    if (stored === 'true') {
      return true
    }
  }
  catch {
    // ignore
  }
  return null
}

/**
 * Resolve whether presentation crossfade is enabled.
 * Precedence: query/localStorage debug override → capacity tier low (unless allowOnLow) → tenant flag → default on.
 * Only known `'low'` forces off when allowOnLow is not true; `null`/unknown keeps tenant default.
 * Soft-defer / registration rematch can pass {@link suppressForHardCut} to force a hard cut.
 */
export function isPresentationCrossfadeEnabled(
  tenantCreativeCrossfade?: boolean | null,
  capacityTier?: 'low' | 'mid' | 'high' | null,
  allowCrossfadeOnLowCapacity?: boolean | null,
  suppressForHardCut?: boolean
): boolean {
  if (suppressForHardCut === true) {
    return false
  }
  const debug = readPresentationCrossfadeDebugOverride()
  if (debug !== null) {
    return debug
  }
  if (isLowCapacityFeatureForcedOff(capacityTier, allowCrossfadeOnLowCapacity)) {
    return false
  }
  if (tenantCreativeCrossfade === false) {
    return false
  }
  return true
}

/**
 * Effective dissolve length (ms). `0` when crossfade is disabled; otherwise tenant ms clamped with shared defaults.
 */
export function resolvePresentationCrossfadeMs(
  tenantCreativeCrossfadeMs: number | null | undefined,
  crossfadeEnabled: boolean
): number {
  if (!crossfadeEnabled) {
    return 0
  }
  return resolvePlayerCreativeCrossfadeMs(tenantCreativeCrossfadeMs ?? undefined)
}

export function setPresentationCrossfadeEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(
      PRESENTATION_CROSSFADE_STORAGE_KEY,
      enabled ? 'true' : 'false'
    )
  }
  catch {
    // ignore
  }
}

/**
 * Fade sticky outgoing out only after incoming paint-ready (`ready`), never on timeout/hard cut.
 */
export function shouldFadeStickyHoldOnClear(
  reason: StickyHoldClearReason,
  crossfadeEnabled: boolean
): boolean {
  return crossfadeEnabled && reason === 'ready'
}

/**
 * Wait for N animation frames (double-rAF is the usual “after paint” barrier).
 * Injectable `raf` for unit tests.
 */
export function waitAnimationFrames(
  count: number,
  raf: (cb: FrameRequestCallback) => number = globalThis.requestAnimationFrame.bind(globalThis)
): Promise<void> {
  const n = Math.max(0, Math.floor(count))
  if (n === 0) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    let left = n
    const step = () => {
      left -= 1
      if (left <= 0) {
        resolve()
        return
      }
      raf(step)
    }
    raf(step)
  })
}

/**
 * Ready-at-commit dissolve when sticky hold would be skipped because incoming is already paint-ready
 * (warm stills). Never for seamless continuous video, registration hold, or slots shorter than the fade.
 */
export function shouldWarmPathPresentationDissolve(opts: {
  crossfadeEnabled: boolean
  useStickyHold: boolean
  incomingAlreadyReady: boolean
  seamlessContinuous: boolean
  registrationHoldActive: boolean
  prevFrame: IQueueFrame | null | undefined
  nextFrame: IQueueFrame | null | undefined
  onAirSlotDurationMs: number
  crossfadeMs?: number
}): boolean {
  const crossfadeMs = opts.crossfadeMs ?? CROSSFADE_MS
  if (!opts.crossfadeEnabled) {
    return false
  }
  if (opts.useStickyHold) {
    return false
  }
  if (!opts.incomingAlreadyReady) {
    return false
  }
  if (opts.seamlessContinuous) {
    return false
  }
  if (opts.registrationHoldActive) {
    return false
  }
  if (opts.prevFrame == null || opts.nextFrame == null) {
    return false
  }
  if (
    sameQueueFrameIdentity(opts.prevFrame, opts.nextFrame)
    && !queueFramesDifferInFacetAssets(opts.prevFrame, opts.nextFrame)
  ) {
    return false
  }
  if (opts.onAirSlotDurationMs <= crossfadeMs) {
    return false
  }
  return true
}
